import React, { useEffect, useId, useRef, useState } from 'react';

type VideoPlayerProps = {
  src: string;
  title: string;
  poster?: string;
  className?: string;
};

const PLAY_EVENT = 'harlan-video-play';

function formatTime(seconds: number) {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export function VideoPlayer({ src, title, poster, className }: VideoPlayerProps) {
  const playerId = useId();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const onOtherPlay = (event: Event) => {
      const detail = (event as CustomEvent<string>).detail;
      if (detail === playerId) return;
      const video = videoRef.current;
      if (!video || video.paused) return;
      video.pause();
    };

    window.addEventListener(PLAY_EVENT, onOtherPlay);
    return () => window.removeEventListener(PLAY_EVENT, onOtherPlay);
  }, [playerId]);

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      window.dispatchEvent(new CustomEvent(PLAY_EVENT, { detail: playerId }));
      void video.play();
    } else {
      video.pause();
    }
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setMuted(video.muted);
  };

  const seek = (value: number) => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = value;
    setCurrent(value);
  };

  const toggleFullscreen = () => {
    const video = videoRef.current;
    if (!video) return;

    if (document.fullscreenElement) {
      void document.exitFullscreen();
      return;
    }

    void video.requestFullscreen();
  };

  return (
    <div className={`video-player${playing ? ' is-playing' : ''}${className ? ` ${className}` : ''}`}>
      <video
        ref={videoRef}
        src={src}
        poster={poster}
        preload="metadata"
        playsInline
        onClick={togglePlay}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onTimeUpdate={(event) => setCurrent(event.currentTarget.currentTime)}
        onLoadedMetadata={(event) => setDuration(event.currentTarget.duration || 0)}
      />

      {!playing && (
        <button className="video-play-overlay" type="button" onClick={togglePlay} aria-label={`Play ${title}`}>
          <span className="video-play-icon" aria-hidden="true" />
        </button>
      )}

      <div className="video-controls">
        <button className="video-ctrl" type="button" onClick={togglePlay} aria-label={playing ? 'Pause' : 'Play'}>
          {playing ? '❚❚' : '▶'}
        </button>

        <span className="video-time">
          {formatTime(current)} / {formatTime(duration)}
        </span>

        <input
          className="video-scrubber"
          type="range"
          min={0}
          max={duration || 0}
          step={0.1}
          value={current}
          aria-label={`Seek ${title}`}
          onChange={(event) => seek(Number(event.target.value))}
        />

        <button className="video-ctrl" type="button" onClick={toggleMute} aria-label={muted ? 'Unmute' : 'Mute'}>
          {muted ? '🔇' : '🔊'}
        </button>

        <button className="video-ctrl" type="button" onClick={toggleFullscreen} aria-label="Fullscreen">
          ⛶
        </button>
      </div>
    </div>
  );
}
